import DefaultButton from '@/components/common/default/DefaultButton';
import DefaultLink from '@/components/common/default/DefaultLink';
import { IndexCollectionCardProps } from '@/types';
import * as Tooltip from '@radix-ui/react-tooltip';
import Image from 'next/image';
import Link from 'next/link';
import { useState } from 'react';

export default function IndexCollectionCard({
  collection,
}: IndexCollectionCardProps) {
  const [tooltipVisible, setTooltipVisible] = useState(false);

  // Safely destructure with default values
  const {
    attributes = {}
  } = collection || {};
  
  const {
    name = '',
    slug = '',
    image = { data: { attributes: { url: '' } } },
    contributor = { data: { attributes: { name: '', image: { data: { attributes: { url: '' } } } } } }
  } = attributes as {
    name?: string;
    slug?: string;
    image?: { data: { attributes: { url: string } } };
    contributor?: { data: { attributes: { name: string; image: { data: { attributes: { url: string } } } } } };
  };

  const imageUrl = image?.data?.attributes?.url || '';
  const contributorName = contributor?.data?.attributes?.name || '';
  const contributorImageUrl = contributor?.data?.attributes?.image?.data?.attributes?.url || '';

  return (
    <div className="tw-flex tw-flex-col tw-w-full tw-bg-white tw-rounded-[20px] tw-shadow-md tw-no-underline">
      <DefaultLink href={`/collections/${slug}`}>
        <Image
          src={imageUrl}
          alt={name}
          width={419}
          height={280}
          className="tw-w-full tw-h-52 tw-object-cover tw-rounded-tr-[20px] tw-rounded-tl-[20px]"
        />
      </DefaultLink>
      <div className="tw-h-fit tw-relative tw-flex nine:tw-p-[20px] nine:tw-min-h-[120px] max-nine:tw-py-4 max-nine:tw-px-2 tw-py-2 tw-border tw-border-solid tw-border-custom-gray tw-border-t-0 tw-rounded-br-[20px] tw-rounded-bl-[20px]">
        <div className="tw-h-fit tw-flex tw-flex-col tw-items-start tw-relative tw-max-w-[75%] tw-font-plusJakartaSans tw-font-medium tw-text-base tw-text-slate-gray">
          <Tooltip.Provider>
            <Tooltip.Root>
              <Tooltip.Trigger asChild>
                <Link
                  href={`/collections/${slug}`}
                  className="tw-no-underline max-nine:tw-hidden"
                >
                  <h2 className="tw-m-0 tw-p-0">
                    <strong className="tw-font-plusJakartaSans tw-font-bold tw-text-lg tw-text-text tw-line-clamp-2 tw-no-underline">
                      {name}
                    </strong>
                  </h2>
                </Link>
              </Tooltip.Trigger>
              <Tooltip.Content
                className="tw-bg-black tw-text-white tw-rounded-md tw-py-1 tw-px-2 tw-text-sm tw-shadow-lg"
                side="top"
                sideOffset={5}
              >
                {name}
                <Tooltip.Arrow className="tw-fill-black" />
              </Tooltip.Content>
            </Tooltip.Root>
          </Tooltip.Provider>
          <DefaultButton
            onClick={() => {
              setTooltipVisible(!tooltipVisible);
              setTimeout(() => {
                setTooltipVisible(false);
              }, 2000);
            }}
            className="nine:tw-hidden tw-flex tw-items-start tw-justify-start"
          >
            <h2 className="tw-text-left tw-font-plusJakartaSans tw-font-bold tw-text-lg tw-text-text tw-line-clamp-1 tw-no-underline max-nine:tw-text-sm">
              {name}
            </h2>
          </DefaultButton>
          {tooltipVisible && (
            <div className="tw-absolute tw-top-full tw-left-0 tw-mt-1 tw-bg-black tw-text-white tw-rounded-md tw-px-3 tw-py-2 tw-text-xs tw-shadow-lg tw-z-20">
              {name.trim()}
            </div>
          )}
          <p className="tw-m-0 tw-text-sm tw-text-slate-gray">
            By {contributorName}
          </p>
        </div>
        <div className="tw-absolute -tw-top-10 tw-right-5 max-nine:tw-right-1 max-nine:-tw-top-9">
          <Image
            src={contributorImageUrl}
            alt={contributorName}
            width={83}
            height={83}
            className="tw-object-cover tw-rounded-full tw-aspect-square max-nine:tw-w-12 max-nine:tw-h-12"
          />
        </div>
      </div>
    </div>
  );
}
